/**
 * `PhoneField`'s mask and its matching validator.
 *
 * The mask runs on every keystroke / paste and rejects anything that doesn't fit,
 * so the `inputMode` / `keyboardType` hints (which only affect mobile soft
 * keyboards) are backed up on web and hardware-keyboard input.
 *
 * They live with the field rather than in a shared utils module so a screen never
 * has to reach across into another module's internals for them — and so the
 * validator cannot drift from the mask that produces the values it checks.
 */

/** US phone: digits only (max 10), progressively masked as `(xxx) xxx-xxxx`. */
export const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);

  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** A complete US phone display mask: (xxx) xxx-xxxx. */
const PHONE_DISPLAY_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/;

/** Whether a formatted phone value is empty or a complete `(xxx) xxx-xxxx` mask. */
export const isValidPhoneDisplay = (value: string): boolean =>
  value === '' || PHONE_DISPLAY_PATTERN.test(value);
